import { useEffect, useState } from 'react';

const cachedScripts = {};

const useScript = (src) => {
  // Keeping track of script loaded and error state
  const [state, setState] = useState({
    loaded: false,
    error: false,
  });

  useEffect(() => {
    // Keep track of mounted state
    let mounted = true;

    // If cachedScripts already includes src that means another instance ...
    // ... of this hook already loaded this script, so no need to load again.
    if (!cachedScripts[src]) {
      cachedScripts[src] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        const onScriptLoad = () => {
          script.removeEventListener('load', onScriptLoad);
          script.removeEventListener('error', onScriptError);
          resolve(src);
        };

        const onScriptError = (e) => {
          // Remove from cachedScripts we can try loading again
          cachedScripts[src] = null;
          script.remove();
          reject(new Error(`Could not load script "${src}": ${e}`));
        };

        script.addEventListener('load', onScriptLoad);
        script.addEventListener('error', onScriptError);
        document.body.appendChild(script);
      });
    }

    cachedScripts[src]
      .then(() => {
        if (mounted) {
          setState({
            loaded: true,
            error: false,
          });
        }
      })
      .catch(() => {
        if (mounted) {
          setState({
            loaded: true,
            error: true,
          });
        }
      });

    // Clean up on hook unmount
    return () => {
      mounted = false;
    };
  }, [src]); // Only re-run effect if script src changes
  return [state.loaded, state.error];
};

export default useScript;
